import React, { useState, useEffect } from "react";
import axios from "axios";
import './App.css';

const API_URL = "/api";

function App() {
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState({ name: "", tagIn: "" });
  const [result, setResult] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parMode, setParMode] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [username, setUsername] = useState(""); // Track username for display
  const [showSettings, setShowSettings] = useState(false);
  

  // Dark mode with user preference in localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) return saved === "true";
    return window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    fetchPlayers();
    fetchHistory();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    // Check if user is logged in
    axios.get(`${API_URL}/auth/me`, { withCredentials: true })
      .then(res => {
        if (res.data.loggedIn) {
          setLoggedIn(true);
          setUsername(res.data.username);
        }
      });
  }, []);

  const fetchPlayers = async () => {
    try {
      const res = await axios.get(`${API_URL}/players`, { withCredentials: true });
      setPlayers(res.data);
    } catch (err) {
      console.error("Failed to fetch players", err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API_URL}/redistributions`, { withCredentials: true });
      setHistory(res.data);
    } catch (err) {
      console.error("Failed to fetch history", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    if (!form.name || !form.tagIn) return;
    try {
      await axios.post(`${API_URL}/players`, {
        name: form.name,
        tagIn: form.tagIn,
        score: form.score
      }, { withCredentials: true });
      setForm({ name: "", tagIn: "" });
      fetchPlayers();
    } catch (err) {
      console.error("Failed to add player", err);
    }
  };

  const handleDeletePlayer = async (id) => {
    try {
      await axios.delete(`${API_URL}/players/${id}`, { withCredentials: true });
      fetchPlayers();
    } catch (err) {
      console.error("Failed to delete player", err);
    }
  };

  const handleScoreChange = (id, score) => {
    // Always treat 0 as a number, not as empty/null
    const parsedScore = score === "" ? null : Number(score);
    setPlayers(players =>
      players.map(player => player.id === id ? { ...player, score: parsedScore } : player)
    );
    axios.patch(`${API_URL}/players/${id}`, { score: parsedScore }, { withCredentials: true })
      .catch(err => {
        console.error("Failed to update score", err);
      });
  };

  // Par-style score adjustment
  const handleParScore = (id, delta) => {
    const player = players.find(p => p.id === id);
    const newScore = (player.score ?? 0) + delta;
    handleScoreChange(id, newScore);
  };

  const handleRedistribute = async () => {
    setLoading(true);
    try {
      // 1. Update all player scores in the DB before redistributing
      await Promise.all(
        players.map(p =>
          axios.patch(`${API_URL}/players/${p.id}`, { score: p.score }, { withCredentials: true })
        )
      );
      // 2. Now call redistribute
      const res = await axios.post(`${API_URL}/redistribute`, {}, { withCredentials: true });
      setResult(res.data);
      fetchHistory();
    } catch (err) {
      console.error("Failed to redistribute tags", err);
    }
    setLoading(false);
  };

  const handleDownloadCSV = () => {
    // Use the same order as displayed in the table
    const displayPlayers = (result.length > 0
      ? result.map(r => players.find(p => p.id === r.id)).filter(Boolean)
      : players
    );

    // CSV header
    let csv = "Name,Tag In,Score,New Tag\n";
    // CSV rows
    csv += displayPlayers.map(p => {
      const newTag = result.length > 0
        ? (result.find(r => r.id === p.id)?.tagOut ?? "")
        : "";
      const score = typeof p.score === "number" ? (p.score === 0 ? "E" : p.score) : "";
      return `"${p.name}","${p.tagIn}","${score}","${newTag}"`;
    }).join("\n");

    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bagtag_users.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLogin = (usernameFromLogin) => {
    setLoggedIn(true);
    setUsername(usernameFromLogin);
    fetchPlayers();
    fetchHistory();
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
      setLoggedIn(false);
      setUsername("");
      setPlayers([]);
      setHistory([]);
      setResult([]);
    } catch (err) {
      alert("Logout failed");
    }
  };

  return (
    <div className={`main-container${darkMode ? " dark" : ""}`}>
      {/* Top bar: Hamburger left, login/logout right */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        {/* Hamburger menu (top left) */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setShowSettings(s => !s)}
            style={{
              background: "none",
              border: "none",
              fontSize: 28,
              cursor: "pointer",
              padding: "0 12px",
              lineHeight: 1,
              color: darkMode ? "#fff" : "#222"
            }}
            aria-label="Settings"
          >
            &#9776; {/* Hamburger icon */}
          </button>
          {showSettings && (
            <div
              style={{
                position: "absolute",
                top: 36,
                left: 0,
                background: darkMode ? "#222" : "#fff",
                color: darkMode ? "#fff" : "#222",
                border: "1px solid #ccc",
                borderRadius: 6,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                padding: 16,
                zIndex: 300,
                minWidth: 220
              }}
            >
              <label className="theme-switch" style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 8 }}>
                <input
                  type="checkbox"
                  checked={parMode}
                  onChange={() => setParMode(p => !p)}
                />
                <span className="theme-slider"></span>
                <span className="theme-label">Par Mode</span>
              </label>
              <label className="theme-switch" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={darkMode}
                  onChange={() => setDarkMode(dm => !dm)}
                />
                <span className="theme-slider"></span>
                <span className="theme-label">Dark mode</span>
              </label>
            </div>
          )}
        </div>
        {/* Login/Logout button (top right) */}
        <div>
          {!loggedIn ? (
            <button onClick={() => setShowAuth(true)}>Sign Up / Log In</button>
          ) : (
            <button
              onClick={handleLogout}
              style={{
                marginRight: 12,
                background: "none",
                border: "none",
                color: "inherit",
                fontWeight: "bold",
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              Welcome, {username}! (Log out)
            </button>
          )}
        </div>
      </div>
      <img
        src="https://img.icons8.com/color/96/000000/tag-window.png"
        alt="Bag Tag"
        style={{ display: "block", margin: "0 auto 12px auto" }}
      />
      <h1>Bag Tag Redistribution</h1>

      <form onSubmit={handleAddPlayer}>
        <input
          name="name"
          placeholder="Name"
          value={form.name}
          onChange={handleChange}
        />
        <input
          name="tagIn"
          type="number"
          placeholder="Tag In"
          value={form.tagIn}
          onChange={handleChange}
        />
        <button type="submit">Add Player</button>
      </form>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Current Players</h2>
        {players.length > 0 && (
          <button
            onClick={async () => {
              if (window.confirm("Are you sure you want to clear all players? This cannot be undone.")) {
                try {
                  await axios.delete(`${API_URL}/players`, { withCredentials: true });
                  fetchPlayers();
                } catch (err) {
                  alert("Failed to clear players.");
                }
              }
            }}
            style={{
              background: "#c00",
              color: "#fff",
              border: "none",
              padding: "6px 16px",
              borderRadius: 4,
              fontWeight: "bold",
              cursor: "pointer"
            }}
          >
            Clear All
          </button>
        )}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Tag In</th>
              <th>Score</th>
              <th>New Tag</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {players.length === 0 ? (
              <tr><td colSpan={5}>No players added.</td></tr>
            ) : (
              (result.length > 0
                ? result.map(r => players.find(p => p.id === r.id)).filter(Boolean)
                : players
              ).map((p) => {
                const newTag = result.length > 0
                  ? (result.find(r => r.id === p.id)?.tagOut ?? "")
                  : "";
                return (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.tagIn}</td>
                    <td>
                      {parMode ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <button
                            type="button"
                            onClick={() => handleParScore(p.id, -1)}
                            style={{ padding: "2px 8px", minWidth: 0 }}
                          >-</button>
                          <span style={{ minWidth: 24, textAlign: "center" }}>
                            {typeof p.score === "number"
                              ? (p.score === 0 ? "E" : p.score)
                              : 0}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleParScore(p.id, 1)}
                            style={{ padding: "2px 8px", minWidth: 0 }}
                          >+</button>
                        </div>
                      ) : (
                        <input
                          type="number"
                          value={p.score ?? ""}
                          disabled={parMode}
                          onChange={e => {
                            // Only update local state, not the DB
                            const newScore = e.target.value === "" ? null : Number(e.target.value);
                            setPlayers(players =>
                              players.map(pl =>
                                pl.id === p.id ? { ...pl, score: newScore } : pl
                              )
                            );
                          }}
                          onBlur={async e => {
                            if (!parMode) {
                              const newScore = e.target.value === "" ? null : Number(e.target.value);
                              try {
                                await axios.patch(`${API_URL}/players/${p.id}`, { score: newScore }, { withCredentials: true });
                                // Optionally, refresh players from DB here
                              } catch (err) {
                                alert("Failed to update score.");
                              }
                            }
                          }}
                        />
                      )}
                    </td>
                    <td>
                      {newTag}
                    </td>
                    <td>
                      <button
                        onClick={() => handleDeletePlayer(p.id)}
                      >Delete</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <button
        onClick={handleRedistribute}
        disabled={players.length === 0 || loading}
        style={{ width: "100%", margin: "18px 0" }}
      >
        {loading ? "Redistributing..." : "Redistribute Tags"}
      </button>

      {result.length > 0 && (
        <button
          onClick={handleDownloadCSV}
          disabled={players.length === 0}
          style={{
            display: "inline-block",
            margin: "0 0 18px 0",
            fontSize: "0.85em",
            padding: "3px 10px",
            width: "auto",
            alignSelf: "flex-start"
          }}
        >
          Download CSV
        </button>
      )}

      <h2>Redistribution History</h2>
      {history.length === 0 ? (
        <p>No redistributions yet.</p>
      ) : (
        <ul>
          {history.map((h) => (
            <li key={h.id}>
              <b>{new Date(h.timestamp).toLocaleString()}</b>
              <ul>
                {h.result.map((r, i) => (
                  <li key={i}>
                    {r.name}: Tag In: {r.tagIn}, Score: {r.score === 0 ? "E" : r.score ?? ""}, <b>Tag Out: {r.tagOut}</b>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {showAuth && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.3)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{
            background: darkMode ? "#222" : "#fff",
            color: darkMode ? "#fff" : "#222",
            padding: 24,
            borderRadius: 8,
            minWidth: 300,
            boxShadow: "0 2px 12px rgba(0,0,0,0.25)"
          }}>
            {showSignUp ? (
              <>
                <SignUpForm onSignUp={() => setShowSignUp(false)} />
                <button onClick={() => setShowSignUp(false)} style={{ marginTop: 8 }}>Back to Login</button>
              </>
            ) : (
              <>
                <LoginForm onLogin={handleLogin} />
                <button onClick={() => setShowSignUp(true)} style={{ marginTop: 8 }}>Create Account</button>
              </>
            )}
            <button onClick={() => setShowAuth(false)} style={{ marginTop: 8 }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuthForm({ onAuth, authType }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = authType === "login" ? `${API_URL}/auth/login` : `${API_URL}/auth/register`;
    try {
      await axios.post(url, { username, password }, { withCredentials: true });
      onAuth();
    } catch (err) {
      alert(`${authType === "login" ? "Login" : "Sign up"} failed: ${err.response?.data?.error || "Unknown error"}`);
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
      <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" />
      <button type="submit">{authType === "login" ? "Log In" : "Sign Up"}</button>
    </form>
  );
}

// Login Form
function LoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/auth/login`, { username, password }, { withCredentials: true });
      onLogin(username); // Pass username up
    } catch (err) {
      alert("Login failed: " + (err.response?.data?.error || "Unknown error"));
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
      <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" />
      <button type="submit">Login</button>
    </form>
  );
}

// Sign Up Form
function SignUpForm({ onSignUp }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/auth/register`, { username, password }, { withCredentials: true });
      alert("Account created! You can now log in.");
      onSignUp();
    } catch (err) {
      alert("Sign up failed: " + (err.response?.data?.error || "Unknown error"));
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" />
      <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" />
      <button type="submit">Sign Up</button>
    </form>
  );
}

export default App;